import { useCallback, useEffect, useRef, useState } from 'react';
import { useSocket, useSocketEvent } from 'socket.io-react-hook';
import { _getToken } from 'src/Config/axiosApi';
import { NotificationEvent } from '../types/notifications.types';

/**
 * Connects to the notifications-websocket service and exposes a
 * stream of new `NotificationEvent`s. The page consumes these and
 * prepends them to its react-query infinite list.
 *
 * Auth: the same JWT pattern as `useUnwindingSocket` —
 * `socket.io-client` reads the current token from a callback so
 * silent refresh / logout propagate without a manual reconnect.
 *
 * Connection model: separate Socket.io connection from `/unwind`
 * (different host) — by design. Notification fanout cannot
 * head-of-line block the market-data WS this way.
 */
export function useNotificationsSocket() {
  const wsUrl = import.meta.env.VITE_NOTIFICATIONS_WS_URL;
  const accessToken = _getToken();

  const { socket } = useSocket(wsUrl || '', {
    enabled: !!wsUrl && !!accessToken,
    transports: ['websocket'],
    auth: (cb: (data: { token: string }) => void) =>
      cb({ token: _getToken() }),
  });

  const [latestEvent, setLatestEvent] = useState<NotificationEvent | null>(
    null,
  );
  const [bufferedCount, setBufferedCount] = useState(0);
  const hasSubscribed = useRef(false);

  // Subscribe on every (re)connect.
  useEffect(() => {
    const subscribe = () => {
      if (!hasSubscribed.current) {
        socket.emit('subscribe_notifications');
        hasSubscribed.current = true;
      }
    };
    const reset = () => {
      hasSubscribed.current = false;
    };

    socket.on('connect', subscribe);
    socket.on('disconnect', reset);
    if (socket.connected) subscribe();
    else reset();

    return () => {
      socket.off('connect', subscribe);
      socket.off('disconnect', reset);
    };
  }, [socket, socket.connected]);

  const { lastMessage } = useSocketEvent<NotificationEvent>(
    socket,
    'notification',
  );

  useEffect(() => {
    if (!lastMessage) return;
    setLatestEvent(lastMessage);
    setBufferedCount((n) => n + 1);
  }, [lastMessage]);

  /** Page calls this after consuming `latestEvent` so the same event
   *  isn't re-applied on re-renders. */
  const acknowledge = useCallback(() => {
    setLatestEvent(null);
  }, []);

  /** Reset the buffered count badge (e.g. after the page refocuses). */
  const resetBufferedCount = useCallback(() => {
    setBufferedCount(0);
  }, []);

  return {
    isConnected: socket.connected,
    latestEvent,
    bufferedCount,
    acknowledge,
    resetBufferedCount,
  };
}
